import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaRobot, FaPaperPlane, FaTimes } from 'react-icons/fa';

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hello! 👋 I'm the CyberNest AI assistant. How can I help you with your web development project today?",
      sender: 'bot',
    },
  ]);
  const [newMessage, setNewMessage] = useState('');

  const chatbotResponses = [
    "I can help you choose the right website solution for your business needs.",
    "Our cyberpunk designs are modern, sleek, and user-friendly.",
    "We specialize in creating responsive websites that look great on all devices.",
    "Would you like to see examples of our portfolio?",
    "Feel free to ask about our pricing plans or custom development options.",
    "Our team can create any type of website - from simple landing pages to complex e-commerce platforms.",
    "What kind of features are you looking for in your website?",
  ];

  const handleSendMessage = (e) => {
    e.preventDefault();
    
    if (newMessage.trim() === '') return;
    
    // Add user message
    const userMessage = {
      id: messages.length + 1,
      text: newMessage,
      sender: 'user',
    };
    
    setMessages(prev => [...prev, userMessage]);
    setNewMessage('');
    
    // Simulate bot response after small delay
    setTimeout(() => {
      const randomResponse = chatbotResponses[Math.floor(Math.random() * chatbotResponses.length)];
      
      const botResponse = {
        id: messages.length + 2,
        text: randomResponse,
        sender: 'bot',
      };
      
      setMessages(prev => [...prev, botResponse]);
    }, 1000);
  };

  // Improved animations with better easing
  const chatVariants = {
    hidden: { 
      opacity: 0, 
      y: 20, 
      scale: 0.95,
      transition: { 
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    visible: { 
      opacity: 1, 
      y: 0, 
      scale: 1,
      transition: { 
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const buttonVariants = {
    rest: { scale: 1 },
    hover: { 
      scale: 1.1,
      transition: { 
        duration: 0.3, 
        ease: [0.22, 1, 0.36, 1] 
      }
    },
    tap: { 
      scale: 0.95,
      transition: { 
        duration: 0.1, 
        ease: [0.22, 1, 0.36, 1] 
      }
    }
  };

  const messageVariants = {
    initial: { 
      opacity: 0, 
      y: 10,
      scale: 0.9
    },
    animate: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: { 
        duration: 0.3,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    exit: { 
      opacity: 0,
      y: 10,
      transition: { 
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  return (
    <div className="fixed bottom-8 right-8 z-40">
      {/* Chat Window */}
      <AnimatePresence>
        {isOpen && (
          <motion.div 
            className="bg-cyber-dark border border-cyber-purple/50 rounded-lg shadow-lg shadow-cyber-purple/10 w-80 sm:w-96 mb-4 max-h-[500px] flex flex-col"
            variants={chatVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {/* Chat header */}
            <div className="flex justify-between items-center p-4 border-b border-cyber-purple/30 bg-cyber-gray/20">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-cyber-purple/20 rounded-full flex items-center justify-center">
                  <FaRobot className="text-cyber-neon text-xl" />
                </div>
                <div>
                  <p className="text-white font-medium">CyberNest Assistant</p>
                  <p className="text-xs text-gray-400">Online</p>
                </div>
              </div>
              <motion.button
                onClick={() => setIsOpen(false)}
                className="text-gray-400 hover:text-cyber-pink"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FaTimes />
              </motion.button>
            </div>
            
            {/* Chat messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-cyber-dark/70">
              <AnimatePresence initial={false}>
                {messages.map(message => (
                  <motion.div
                    key={message.id}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                    variants={messageVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    layout
                  >
                    <div 
                      className={`
                        max-w-[80%] rounded-lg p-3
                        ${message.sender === 'user' 
                          ? 'bg-cyber-pink/20 border border-cyber-pink/30 text-white rounded-br-none' 
                          : 'bg-cyber-neon/10 border border-cyber-neon/30 text-white rounded-bl-none'}
                      `}
                    >
                      {message.text}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
            
            {/* Chat input */}
            <form onSubmit={handleSendMessage} className="p-3 border-t border-cyber-purple/30 flex gap-2">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type a message..."
                className="cyber-input text-sm py-2 flex-1"
              />
              <motion.button
                type="submit"
                className="bg-cyber-neon/20 hover:bg-cyber-neon/30 text-cyber-neon p-2 rounded-sm border border-cyber-neon/30"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <FaPaperPlane />
              </motion.button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Chat button */}
      <motion.button
        onClick={() => setIsOpen(!isOpen)}
        className="group bg-cyber-purple hover:bg-cyber-pink text-white w-14 h-14 rounded-full flex items-center justify-center shadow-lg shadow-cyber-purple/20 relative overflow-hidden border border-cyber-purple/50"
        variants={buttonVariants}
        whileHover="hover"
        whileTap="tap"
        animate={{ 
          boxShadow: isOpen 
            ? "0 0 0 0 rgba(157, 78, 221, 0)" 
            : "0 0 0 8px rgba(157, 78, 221, 0.2)"
        }}
        transition={{
          boxShadow: {
            repeat: isOpen ? 0 : Infinity,
            repeatType: "reverse",
            duration: 1.5,
            ease: [0.22, 1, 0.36, 1]
          }
        }}
      >
        <FaRobot className="text-2xl" />
        
        {/* Ripple effect */}
        <motion.span
          className="absolute inset-0 rounded-full bg-white"
          initial={{ scale: 0, opacity: 0.3 }}
          animate={{ 
            scale: 2,
            opacity: 0,
          }}
          transition={{
            repeat: Infinity,
            duration: 2,
            ease: "linear",
          }}
          style={{ originX: 0.5, originY: 0.5 }}
        />
      </motion.button>
    </div>
  );
};

export default Chatbot; 